import React, { useState, useEffect } from 'react';
import { getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial, toggleTestimonialStatus, getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import TestimonialForm from '../forms/TestimonialForm';
import toast from 'react-hot-toast';

const TestimonialsModule = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialCategories, setTestimonialCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const columns = ['Avatar', 'Name', 'Company', 'Category', 'Rating', 'Status', 'Actions'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [testimonialsResponse, categoriesResponse] = await Promise.all([
        getTestimonials(),
        getCategories()
      ]);
      
      let testimonialsData = testimonialsResponse.data || testimonialsResponse || [];
      
      // Ensure all testimonials have a category
      testimonialsData = testimonialsData.map(item => ({
        ...item,
        category: item.category || item.categoryName || 'General',
        categoryName: item.categoryName || item.category || 'General'
      }));
      
      setTestimonials(testimonialsData);
      
      let categoriesData = [];
      if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        categoriesData = categoriesResponse.data.filter(cat => cat.type === 'testimonial');
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesData = categoriesResponse.filter(cat => cat.type === 'testimonial');
      }
      
      setTestimonialCategories(categoriesData);
      
      console.log('Testimonials loaded:', testimonialsData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching testimonials');
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await deleteTestimonial(id);
        toast.success('Testimonial deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Error deleting testimonial');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleTestimonialStatus(id);
      toast.success(`Testimonial ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Error toggling testimonial status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating);
  };

  // Updated filtering logic - show all when 'all' is selected
  const filteredTestimonials = selectedCategory === 'all'
    ? testimonials
    : testimonials.filter(testimonial => {
        const testimonialCategory = testimonial.categoryName || testimonial.category || 'General';
        return testimonialCategory === selectedCategory;
      });

  if (loading) {
    return <LoadingSpinner message="Loading testimonials..." />;
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Testimonials Management</h2>
        <p style={{ color: '#5A6E7F' }}>Manage client testimonials and feedback</p>
      </div>

      {/* Category Filter Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: '#F7F9FC',
        borderRadius: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, color: '#0A192F' }}>
            <i className="fas fa-filter"></i> Filter by Category:
          </span>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              background: 'white',
              fontSize: '0.875rem',
              minWidth: '180px'
            }}
          >
            <option value="all">📋 All Categories ({testimonials.length})</option>
            {testimonialCategories.map((cat) => (
              <option key={cat._id} value={cat.name}>
                {cat.icon && <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>}
                {cat.name} ({testimonials.filter(t => (t.categoryName || t.category) === cat.name).length})
              </option>
            ))}
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '0.5rem 1rem',
              background: viewMode === 'grid' ? '#2C7A7B' : '#FFFFFF',
              color: viewMode === 'grid' ? 'white' : '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-th-large"></i> Grid View
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '0.5rem 1rem',
              background: viewMode === 'table' ? '#2C7A7B' : '#FFFFFF',
              color: viewMode === 'table' ? 'white' : '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-table"></i> Table View
          </button>
        </div>
      </div>

      {/* Add Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ 
            padding: '0.6rem 1.2rem', 
            background: '#2C7A7B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '10px', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontWeight: 500
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
        >
          <i className="fas fa-plus"></i> Add New Testimonial
        </button>
      </div>

      {/* Stats */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#5A6E7F', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-comment" style={{ color: '#2C7A7B' }}></i>
          <strong>{filteredTestimonials.length}</strong> {filteredTestimonials.length === 1 ? 'testimonial' : 'testimonials'} found
          {selectedCategory !== 'all' && <span> in <strong>{selectedCategory}</strong></span>}
        </p>
      </div>

      {filteredTestimonials.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '20px', border: '2px dashed #E2E8F0' }}>
          <i className="fas fa-comment" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
          <p style={{ color: '#5A6E7F', marginBottom: '1rem', fontSize: '1.1rem' }}>No testimonials found in this category</p>
          <button onClick={() => { setEditingItem(null); setShowModal(true); }} style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>
            <i className="fas fa-plus"></i> Create New Testimonial
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredTestimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial._id}
              testimonial={testimonial}
              getFullImageUrl={getFullImageUrl}
              renderStars={renderStars}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        // Table view code remains the same...
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTestimonials.map((testimonial) => (
                <tr key={testimonial._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem' }}>
                    {testimonial.avatar ? (
                      <img src={getFullImageUrl(testimonial.avatar)} alt={testimonial.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', background: '#2C7A7B', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        {testimonial.name?.charAt(0) || 'U'}
                      </div>
                    )}
                   </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>
                    {testimonial.name}
                    <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>{testimonial.position}</div>
                   </td>
                  <td style={{ padding: '1rem' }}>{testimonial.company || '-'}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      background: '#E3F2FD',
                      borderRadius: '20px',
                      fontSize: '0.7rem'
                    }}>
                      {testimonial.category || testimonial.categoryName || 'General'}
                    </span>
                   </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ fontSize: '0.9rem' }}>{renderStars(testimonial.rating || 5)}</span>
                   </td>
                  <td style={{ padding: '1rem' }}>
                    <button onClick={() => handleToggle(testimonial._id, testimonial.isActive)} style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: testimonial.isActive ? '#D4EDDA' : '#F8D7DA', color: testimonial.isActive ? '#155724' : '#721C24', cursor: 'pointer', fontSize: '0.75rem' }}>
                      {testimonial.isActive ? 'Active' : 'Inactive'}
                    </button>
                   </td>
                  <td style={{ padding: '1rem' }}>
                    <button onClick={() => handleEdit(testimonial)} style={{ padding: '0.35rem 0.8rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '0.75rem' }}>
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button onClick={() => handleDelete(testimonial._id)} style={{ padding: '0.35rem 0.8rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TestimonialForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchData}
          testimonialCategories={testimonialCategories}
        />
      )}
    </div>
  );
};

// TestimonialCard component remains the same...
const TestimonialCard = ({ testimonial, getFullImageUrl, renderStars, onEdit, onDelete, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        border: '1px solid #E2E8F0',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 1 }}>
        <button onClick={() => onToggle(testimonial._id, testimonial.isActive)} style={{
          padding: '0.3rem 0.8rem',
          borderRadius: '20px',
          border: 'none',
          background: testimonial.isActive ? '#10B981' : '#EF4444',
          color: 'white',
          cursor: 'pointer',
          fontSize: '0.7rem',
          fontWeight: 600
        }}>
          {testimonial.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      <div style={{ padding: '1.5rem' }}>
        {testimonial.category && (
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{
              background: '#E3F2FD',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.7rem',
              color: '#2C7A7B',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem'
            }}>
              <i className="fas fa-tag"></i> {testimonial.categoryName || testimonial.category}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          {testimonial.avatar ? (
            <img src={getFullImageUrl(testimonial.avatar)} alt={testimonial.name} style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
              {testimonial.name?.charAt(0) || 'U'}
            </div>
          )}
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0A192F', marginBottom: '0.25rem' }}>{testimonial.name}</h3>
            <p style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>{testimonial.position}</p>
            {testimonial.company && <p style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{testimonial.company}</p>}
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <span style={{ fontSize: '1rem' }}>{renderStars(testimonial.rating || 5)}</span>
        </div>

        <p style={{
          fontSize: '0.9rem',
          color: '#5A6E7F',
          lineHeight: '1.6',
          marginBottom: '1rem',
          fontStyle: 'italic',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          "{testimonial.content?.substring(0, 150)}..."
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E2E8F0' }}>
          <button onClick={() => onEdit(testimonial)} style={{ flex: 1, padding: '0.5rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }} onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}>
            <i className="fas fa-edit"></i> Edit
          </button>
          <button onClick={() => onDelete(testimonial._id)} style={{ flex: 1, padding: '0.5rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }} onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}>
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsModule;